/**
 * PER-07 — la personnalisation consiste à **injecter des variables**, pas à
 * recompiler.
 *
 * Tailwind v4 transforme le bloc `@theme` de `src/index.css` en vraies
 * propriétés personnalisées CSS posées sur `:root`, et chaque utilitaire
 * (`bg-rideau-700`, `outline-rideau-600`) les lit par `var()`. Réécrire ces
 * onze propriétés suffit donc à recolorer toute l'application — barres,
 * boutons, anneaux de focus, et jusqu'aux sièges du plan de salle, qui sont du
 * SVG coloré par les mêmes variables.
 *
 * Deux façons de les poser, une seule mécanique :
 *
 *  - **globalement** : en style en ligne sur `<html>` (`FournisseurApparence`).
 *    Le style en ligne l'emporte sur la règle `:root` de la feuille compilée,
 *    sans `!important` et sans lutte de spécificité ;
 *  - **localement** : en `style` sur un conteneur (`Apercu`). Les variables CSS
 *    héritent, donc tout ce qui est à l'intérieur du conteneur — et rien
 *    d'autre — prend la nouvelle apparence. C'est ce qui rend l'aperçu de
 *    PER-06 possible **sans rien appliquer**.
 *
 * Ce fichier est pur : il ne fabrique que l'objet de variables.
 */

import type { CSSProperties } from 'react'

import { TONS, type Echelle } from './api'

/** Nom de la propriété CSS d'un ton — l'échelle historique s'appelle « rideau ». */
export function variableDuTon(ton: number): string {
  return `--color-rideau-${ton}`
}

/**
 * Les variables à poser pour une échelle et une police données.
 *
 * `CSSProperties` accepte les propriétés personnalisées via un index de
 * chaînes ; React les écrit telles quelles dans l'attribut `style`.
 */
export function variablesApparence(echelle: Echelle, pilePolice?: string): CSSProperties {
  return variablesBrutes(echelle, pilePolice)
}

function variablesBrutes(echelle: Echelle, pilePolice?: string): Record<string, string> {
  const variables: Record<string, string> = {}

  for (const ton of TONS) {
    const valeur = echelle[String(ton)]
    /* Un ton manquant n'est PAS remplacé par une couleur inventée : on laisse
       la valeur compilée d'origine reprendre la main. Mieux vaut un écran
       partiellement personnalisé qu'un écran dont une nuance vient de nulle
       part. */
    if (valeur !== undefined) variables[variableDuTon(ton)] = valeur
  }

  /**
   * ★ Les deux jetons DÉRIVÉS du plan de salle, posés explicitement.
   *
   * `index.css` les déclare `var(--color-rideau-700)` et `var(--color-rideau-950)`,
   * ce qui suffit quand on réécrit l'échelle sur `:root` — c'est le cas du
   * fournisseur global. Mais une propriété personnalisée est **résolue à
   * l'endroit où elle est déclarée**, puis héritée déjà résolue : redéfinir
   * `--color-rideau-700` sur un conteneur plus bas ne recalcule pas
   * `--color-siege-selectionne`, restée figée sur la valeur de `:root`.
   *
   * Sans ces deux lignes, l'aperçu de PER-06 montrait un plan de salle resté
   * dans l'ancienne couleur pendant que les boutons juste à côté avaient déjà
   * changé — précisément le genre de détail qui fait douter de tout l'écran.
   */
  const ton700 = echelle['700']
  const ton950 = echelle['950']
  if (ton700 !== undefined) variables['--color-siege-selectionne'] = ton700
  if (ton950 !== undefined) variables['--color-siege-selectionne-bord'] = ton950

  if (pilePolice !== undefined && pilePolice.trim() !== '') {
    variables['--font-sans'] = pilePolice
  }

  return variables
}

/**
 * Applique les variables sur un élément (typiquement `<html>`), et rend la
 * fonction qui les retire. Utilisé par le fournisseur global, où l'on ne rend
 * pas de balise autour de l'application entière.
 */
export function poserVariables(
  cible: HTMLElement,
  echelle: Echelle,
  pilePolice?: string,
): () => void {
  const variables = variablesBrutes(echelle, pilePolice)
  for (const [nom, valeur] of Object.entries(variables)) {
    cible.style.setProperty(nom, valeur)
  }
  return () => {
    for (const nom of Object.keys(variables)) cible.style.removeProperty(nom)
  }
}
